import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as THREE from 'three';
import { COMMANDS } from '../../../utils/commonProperties';
import { setCommandMessage, resetOperation, setCommandType, setOperationData } from '../../../redux/operationSlice';
import {  updateComponent, updateFeeder, incrementalPowerUpdate } from '../../../redux/simpleSupplySlice';
import { addWire } from '../../../redux/wireSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { COMPONENT_KINDS, FEEDER_KINDS } from '../../../types/SimpleSupplyPoint';
import { createLightFixture } from '../../../factory/symbols/createLightFixtureSymbols';
import { updatePanelTitles } from '../../../modules/wiring';
import { useComponentWiring, WireMarkerGenerator, fixtureLineModeRef } from '../../../modules/wiring';
import useSnapPoints from '../../../shared/snap/useSnapPoints';
import useFixtureGizmo from './useFixtureGizmo';
import parseColor from '../../../utils/parseColor';
import { selectActiveFloorPlan } from '../../../redux/floorPlanSlice';
import { store as appStore } from '../../../redux/store';
import { getActiveFloorPlanId, getGlobalDocumentBounds } from '../../../utils/getActiveFloorPlanId';
import { attachTesisatToFloorPlanMesh } from '../../../modules/fpSync';
import { addTesisat } from '../../../redux/tesisatSlice';
import { addTesisatIdToFloorPlan } from '../../../redux/floorPlanSlice';
import { worldToLocal } from '../../../shared/coordinates/floorPlanOrigin';

/**
 * 🆕 SIMPLE LIGHTING FIXTURE HOOK
 * Aydınlatma armatürlerini yeni simpleSupply yapısına entegre eder
 * Legacy useDrawLightFixture'e alternatif - sadece aydınlatma buatları ve hatlarına armatür ekler
 */
const useFixture = (scene, camera, renderer, undoRedoManagerRef) => {
  const dispatch = useDispatch();
  const { commandType, data: operationData } = useSelector((state) => state.operation);
  // simpleSupply: useSelector kaldırıldı — event handler'larda lazy read
  // useEffect dependency için sadece components.length (scalar) kullan
  const supplyComponentsLength = useSelector((state) => state.simpleSupply.components.length);
  const settings = useSelector((state) => state.settings);
  const activeFloorPlan = useSelector(selectActiveFloorPlan);
  const { snapPoints } = useSnapPoints(scene);

  const dynamicSnapPoints = useRef([]);
  const wiringActiveRef = useRef(false);
  const sceneRef = useRef(null);
  // ✅ L tuşu ile "düz mod": true ise hat (preview + kalıcı) spline yerine düz polyline.
  // Paylaşımlı ref (wiring modülünde) — preview useBaseWiring'de, kalıcı hat burada okur.
  // Toggle kalıcı — çizimler arası korunur (kullanıcı tekrar L ile kapatana kadar).
  const lineModeRef = fixtureLineModeRef;

  // ✅ Fixture artık mouse pozisyonunda merkezleniyor - offset kaldırıldı
  // Eski offset fonksiyonu kaldırıldı - armatürler zaten createLightFixture'da merkezleniyor

  // ✅ Fixture wire color
  const parsedWireColor = parseColor(settings?.fixture?.wireColor ?? '#0045ff');
  const wireColor = parsedWireColor.colorNumber;

  // ✅ Fixture item types
  const lightTypes = [
    { name: 'normal', type: 'normal', power: 10 },
    { name: 'etanj', type: 'etanj', power: 10 },
    { name: 'Kare Led Spot', type: 'Kare Led Spot', power: 10 },
    { name: 'Asma Tavan Kare Floresans', type: 'Asma Tavan Kare Floresans', power: 10 },
    { name: 'Yuvarlak Led Spot', type: 'Yuvarlak Led Spot', power: 10 },
    { name: 'tablo', type: 'tablo', power: 10 },
    { name: 'aplik', type: 'aplik', power: 10 }
  ];

  // ✅ Fixture Configuration
  const fixtureConfig = {
    commandType: COMMANDS.DRAW_FIXTURE,
    itemTypeName: 'fixture',
    wireColor: wireColor,
    allowSpaceKey: true, // ✅ Space ile fixture ekleme
    skipItemExecution: false, // ✅ Space ile ekleme yapılıyor, execute etmeli
    simplifyCollinearVertices: false, // ✅ Spline curves için vertex silmeyi devre dışı bırak
    markerCount: 2, // ✅ Aydınlatma/fixture hattı 2 iletkenli (preview ile kalıcı tutarlı)
    createSymbol: (position, direction, angle, isPreview, itemType, extraData = {}) => {
      const fixturePreviewHex = settings?.fixture?.previewColor || settings?.fixture?.color || '#ffffff';
      const fixturePreviewNum = typeof fixturePreviewHex === 'string' ? parseInt(fixturePreviewHex.replace('#', ''), 16) : fixturePreviewHex;
      const fixtureColorHex = settings?.fixture?.color || '#ffffff';
      const fixtureColorNum = typeof fixtureColorHex === 'string' ? parseInt(fixtureColorHex.replace('#', ''), 16) : fixtureColorHex;

      // ✅ Toplam rotasyonu hesapla: wire direction + kullanıcı rotasyonu (R tuşu)
      const userRotation = (extraData.rotateValue || 0) * Math.PI / 180; // derece -> radyan
      const totalRotation = angle + userRotation;

      return createLightFixture(
        null,
        position,
        direction,
        fixtureColorNum,
        fixturePreviewNum,
        isPreview,
        itemType?.type || 'normal',
        null,
        totalRotation // ✅ Toplam rotasyonu geçir
      );
    },
    // ✅ Fixture için command, feeder, component yok - tümü handleWireEnd'de manuel oluşturuluyor
    createCommand: null,
    createFeederData: null,
    createComponentData: null,
    itemTypes: lightTypes,
    onKeyDown: null
  };

  /**
   * 🔌 Simple Supply'ye aydınlatma armatürü ekleme
   */
  const addLightingFixtureToSimpleSupply = (fixtureData) => {
    const { parentLightingLoadId, fixtures, feederData, totalPower } = fixtureData;

    // 1. Mevcut aydınlatma load'unu bul ve gücünü artır
    const existingLoad = appStore.getState().simpleSupply.components.find(c => c.id === parentLightingLoadId);
    if (!existingLoad) {
      console.warn('[Fixture DEBUG] Load not found! All load IDs:', appStore.getState().simpleSupply.components.filter(c => c.kind === 'load').map(c => c.id));
      return;
    }

    const updatedLoad = {
      ...existingLoad,
      electrical: {
        ...existingLoad.electrical,
        installedPower: existingLoad.electrical.installedPower + totalPower,
        demandPower: existingLoad.electrical.demandPower + totalPower,
        current: (existingLoad.electrical.installedPower + totalPower) / 220
      },
      metadata: {
        ...existingLoad.metadata,
        fixtures: [
          ...(existingLoad.metadata?.fixtures || []),
          ...fixtures.map(fixture => ({
            id: generateUniqueId('fixture'),
            type: fixture.type,
            position: fixture.position,
            power: fixture.power || 9,
            uuid: fixture.uuid
          }))
        ]
      }
    };

    // 2. Bu load'ı besleyen feeder'ı bul ve gücünü artır
    const lightingFeeder = appStore.getState().simpleSupply.feeders.find(f => f.to === parentLightingLoadId);
    if (lightingFeeder) {
      // Feeder'ın electrical alanını güncelle
      dispatch(updateFeeder({
        id: lightingFeeder.id,
        changes: {
          electrical: {
            ...lightingFeeder.electrical,
            installedPower: lightingFeeder.electrical.installedPower + totalPower,
            demandPower: lightingFeeder.electrical.demandPower + totalPower,
            current: (lightingFeeder.electrical.installedPower + totalPower) / 220
          }
        }
      }));
    }

    // 3. Load'u güncelle
    dispatch(updateComponent({
      id: parentLightingLoadId,
      changes: {
        electrical: updatedLoad.electrical,
        metadata: updatedLoad.metadata
      }
    }));

    // 4. İnkremental güç artışını parent chain'e propagate et
    dispatch(incrementalPowerUpdate({
      startComponentId: parentLightingLoadId,
      powerDelta: totalPower
    }));

    // Armatür feeder'ı oluştur
    const fixtureFeeder = {
      id: feederData.id || generateUniqueId('lighting_fixture_feeder'),
      from: parentLightingLoadId,
      to: 'fixtures', // Armatürler doğrudan load'a bağlı
      kind: FEEDER_KINDS.LIGHTING_CIRCUIT,
      vertices: feederData.vertices || [],
      electrical: {
        installedPower: totalPower,
        current: totalPower / 220,
        voltage: 220,
        phaseCount: 1
      },
      cable: {
        type: 'NYM 2x1.5', // Armatür kablosu
        crossSection: 1.5,
        length: feederData.cable?.length || 0,
        currentCap: 20
      },
      metadata: {
        wireType: 'lightingFixture',
        color: settings?.fixture?.wireColor || '#0045ff',
        layer: 'lightingFixture',
        parentLightingLoadId
      },
      wireId: feederData.wireId
    };

    return fixtureFeeder;
  };


  /**
   * 🔚 Wire bitiş işlemi - Simple Supply entegrasyonu
   */
  const handleWireEnd = (vertices, fixtures, source) => {
    // Minimum wire gereksinimlerini kontrol et
    if (!vertices || vertices.length < 2) {
      console.warn('[Fixture handleWireEnd DEBUG] Not enough vertices, returning');
      return;
    }

    if (!fixtures || fixtures.length === 0) {
      console.warn('[Fixture handleWireEnd DEBUG] No fixtures, returning');
      return;
    }

    // Aydınlatma load'ını bul - vertices'in ilk noktasındaki snap point'ten
    let parentLightingLoadId = null;
    let lightingLineWireId = null;

    // Eğer source varsa ve valid ise, ondan al
    if (source && source.type === "lightingBuat") {
      parentLightingLoadId = source.parent?.userData?.lightingLoadId || source.userData?.lightingLoadId;
      lightingLineWireId = source.parent?.userData?.wireID || source.userData?.wireID;
    } else if (source && source.type === "lightSwitch") {
      // Aydınlatma anahtarından başlatıldı - parent wire'ı bul
      // Sahneyi tara ve bu anahtarı içeren wire'ı bul
      sceneRef.current.traverse(obj => {
        if ((obj.userData?.function === 'wireLighting' || obj.userData?.function === 'simpleLighting') &&
            obj.userData?.lightSwitches?.includes(source.userData?.id)) {
          parentLightingLoadId = obj.userData?.lightingLoadId;
          lightingLineWireId = obj.userData?.id;
        }
      });
    } else if (source && source.type === 'cableShaftConnection' && operationData?.continueFromShaft) {
      // ✅ Kablo bacasından devam eden fixture hattı
      // 1) Doğrudan operationData'dan al (CableShaftTypeMenu'den geçirildi)
      parentLightingLoadId = operationData?.parentLightingLoadId || null;
      // 2) Yoksa Redux'tan bul (sourcePanelId + lightingSubType ile)
      if (!parentLightingLoadId) {
        const sourceCable = operationData?.sourceCable;
        const sourcePanelId = sourceCable?.sourcePanelId;
        const subType = sourceCable?.lightingSubType || operationData?.initialItemType;
        if (sourcePanelId && subType) {
          const matchingLoad = appStore.getState().simpleSupply.components.find(c =>
            c.kind === COMPONENT_KINDS.LOAD &&
            c.parentComponentId === sourcePanelId &&
            c.metadata?.lightingSubType === subType
          );
          if (matchingLoad) {
            parentLightingLoadId = matchingLoad.id;
          }
        }
        // 3) Son fallback: lightingSubType ile herhangi bir load bul
        if (!parentLightingLoadId && subType) {
          const anyMatchingLoad = appStore.getState().simpleSupply.components.find(c =>
            c.kind === COMPONENT_KINDS.LOAD &&
            c.metadata?.lightingSubType === subType
          );
          if (anyMatchingLoad) {
            parentLightingLoadId = anyMatchingLoad.id;
          }
        }
      }
      // lightingLineWireId'yi mevcut load'dan al
      if (parentLightingLoadId) {
        const loadForWireId = appStore.getState().simpleSupply.components.find(c => c.id === parentLightingLoadId);
        lightingLineWireId = loadForWireId?.metadata?.wireId || null;
      }
      // 4) Son fallback: GT panelinin lighting load'unu bul (subType ile eşleştir)
      if (!parentLightingLoadId) {
        const gtPanel = appStore.getState().simpleSupply.components.find(c =>
          (c.kind === COMPONENT_KINDS.DIST_PANEL || c.kind === COMPONENT_KINDS.PANEL) &&
          c.electrical?.purposeOfUse === 'Müşterek'
        );
        if (gtPanel) {
          const subType = operationData?.sourceCable?.lightingSubType || operationData?.initialItemType;
          // Önce subType ile eşleşen load'u bul (Merdiven vs Asansör ayırt etmek için)
          let gtLightingLoad = subType
            ? appStore.getState().simpleSupply.components.find(c =>
                c.kind === COMPONENT_KINDS.LOAD &&
                c.parentComponentId === gtPanel.id &&
                c.metadata?.lightingSubType === subType
              )
            : null;
          // subType yoksa veya eşleşme bulunamadıysa herhangi bir lighting load al
          if (!gtLightingLoad) {
            gtLightingLoad = appStore.getState().simpleSupply.components.find(c =>
              c.kind === COMPONENT_KINDS.LOAD &&
              c.parentComponentId === gtPanel.id &&
              (c.branchType === 'lighting' || c.metadata?.lightingSubType)
            );
          }
          if (gtLightingLoad) {
            parentLightingLoadId = gtLightingLoad.id;
          }
        }
      }
    }

    // Source yoksa veya eksikse, sahneyi tara ve uygun lighting load bul
    if (!parentLightingLoadId) {
      // İlk vertex yakınındaki lighting buat'ları veya anahtarları ara
      const firstVertex = vertices[0];
      let closestLightingObject = null;
      let minDistance = 50; // 50 birim yakınlık
      let foundObjects = []; // Debug için

      sceneRef.current.traverse(obj => {
        if (obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle' || obj.userData?.type === 'lightSwitch') {
          const distance = obj.position.distanceTo(new THREE.Vector3(firstVertex.x, firstVertex.y, firstVertex.z || 0));
          foundObjects.push({ type: obj.userData?.type, distance, position: obj.position.clone() });
          if (distance < minDistance) {
            minDistance = distance;
            closestLightingObject = obj;
          }
        }
      });


      if (closestLightingObject) {
        if (closestLightingObject.userData?.type === 'lightSwitch') {
          // Anahtar bulundu, parent wire'ı bul
          sceneRef.current.traverse(obj => {
            if ((obj.userData?.function === 'wireLighting' || obj.userData?.function === 'simpleLighting') &&
                obj.userData?.lightSwitches?.includes(closestLightingObject.userData?.id)) {
              parentLightingLoadId = obj.userData?.lightingLoadId;
              lightingLineWireId = obj.userData?.id;
            }
          });
        } else {
          // Buat bulundu
          parentLightingLoadId = closestLightingObject.parent?.userData?.lightingLoadId ||
                                closestLightingObject.userData?.lightingLoadId;
          lightingLineWireId = closestLightingObject.parent?.userData?.wireID ||
                              closestLightingObject.userData?.wireID;
        }
 
      }
    }

    if (!parentLightingLoadId) {
      return;
    }

    // Kablo uzunluğunu hesapla - vertex format uyumluluğu sağla
    let totalLength = 0;
    for (let i = 0; i < vertices.length - 1; i++) {
      const v1 = vertices[i];
      const v2 = vertices[i + 1];

      // Vertex'leri Three.js Vector3 formatına dönüştür
      const vec1 = v1.x !== undefined ? new THREE.Vector3(v1.x, v1.y, v1.z || 0) : v1;
      const vec2 = v2.x !== undefined ? new THREE.Vector3(v2.x, v2.y, v2.z || 0) : v2;

      totalLength += vec1.distanceTo(vec2);
    }

    // Toplam güç hesapla
    const totalPower = fixtures.reduce((sum, fixture) => sum + (fixture.power || 9), 0);

    // ✅ Fixture sembollerini sahneye ekle (Space ile eklenen geçici fixture'lar kalıcı hale getiriliyor)
    const fixtureColorHex = settings?.fixture?.color || '#ffffff';
    const fixtureColorNum = typeof fixtureColorHex === 'string' ? parseInt(fixtureColorHex.replace('#', ''), 16) : fixtureColorHex;

    const createdFixtures = [];
    fixtures.forEach((fixtureItem) => {
      // Fixture position ve direction bilgisi item'dan geliyor
      const position = fixtureItem.position;
      const direction = fixtureItem.direction;
      const fixtureType = fixtureItem.type || 'normal';
      const fixturePower = fixtureItem.power || 10;

      // ✅ Toplam rotasyonu hesapla: wire direction + kullanıcı rotasyonu (R tuşu)
      const directionAngle = Math.atan2(direction.y, direction.x);
      const userRotation = (fixtureItem.rotateValue || 0) * Math.PI / 180; // derece -> radyan
      const totalRotation = directionAngle + userRotation;

      let lightFixture;
      if (fixtureItem._sceneObj) {
        // Space ile zaten sahneye eklendi — tekrar oluşturmaya gerek yok
        lightFixture = fixtureItem._sceneObj;
      } else {
        // Fixture sembolü oluştur (ör. Enter ile tek seferde)
        lightFixture = createLightFixture(
          sceneRef.current,
          position,
          direction,
          fixtureColorNum,
          fixtureColorNum,
          false,
          fixtureType,
          null,
          totalRotation
        );
        sceneRef.current.add(lightFixture);
      }

      // userData'yı güncelle (rotate alanını ekle)
      const fpId = getActiveFloorPlanId(position, getGlobalDocumentBounds()) || null;
      lightFixture.userData = {
        ...lightFixture.userData,
        id: fixtureItem.id || generateUniqueId('fixture'),
        type: 'LightFixture',
        isSelectable: true,
        originalColor: fixtureColorNum,
        connectedFrom: null,
        connectedTo: position.clone(),
        power: fixturePower,
        fixtureType: fixtureType,
        // buildLightFixture'in createLightFixture'a verdigi 7. parametre `item.symbol`.
        // useSceneTesisatSync extractTesisatPayload da `ud.symbol`'u okur — `fixtureType`
        // ile aynı değer (örn. 'Kare Led Spot', 'etanj'), ayrı bir alanda da tutuyoruz
        // ki rebuild zinciri parametreyi kaybetmesin.
        symbol: fixtureType,
        rotate: totalRotation,
        basePoint: { x: position.x, y: position.y },
        floorPlanId: fpId,
      };

      // Mesh-FP: fixture'i fpMesh'e ekle (socket pattern'i ile ayni)
      attachTesisatToFloorPlanMesh(sceneRef.current, lightFixture, fpId);

      // tesisatSlice'a yaz — JBZ load sonrasi Redux-driven rebuild (socket pattern'i).
      // sceneSerializer fpMesh-local saklarsa save/load arasi fpMesh.position shift
      // ettiginde world kayar; Redux WORLD saklayarak useTesisatRebuild bunu duzeltir.
      const fixtureId = lightFixture.userData.id;
      const fixtureWorldPos = { x: position.x, y: position.y, z: position.z || 0 };
      const tesisatPayload = {
        id: fixtureId,
        type: 'LightFixture',
        position: fixtureWorldPos,
        localPosition: fpId ? worldToLocal(fixtureWorldPos, fpId) : null,
        rotation: totalRotation,
        rotateValue: fixtureItem.rotateValue || 0,
        fixtureType,
        symbol: fixtureType,
        power: fixturePower,
        color: fixtureColorNum,
        floorPlanId: fpId,
        _v: 2,
      };
      dispatch(addTesisat(tesisatPayload));
      if (fpId) {
        dispatch(addTesisatIdToFloorPlan({
          tabId: 'elektrik',
          floorPlanId: fpId,
          itemId: fixtureId,
        }));
      }

      createdFixtures.push(lightFixture);
    });

    // Wire ID oluştur ve Redux wireSlice'a kaydet
    const wireId = generateUniqueId("wireLightingFixture");
    // ✅ Redux addWire payload — undo/redo'da (FixtureCommand) tekrar dispatch edilebilsin
    // diye sceneData'da saklanır.
    const wireAddPayload = {
      id: wireId,
      type: "curvedLine",
      vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
      layer: "lightingFixture",
      color: settings?.fixture?.wireColor || "#0045ff",
      userData: {
        selectable: true,
        parent: "lightingFixture",
        parentLightingLoadId,
        lightingLineWireId,
        lineMode: lineModeRef.current, // ✅ Düz mod bayrağı (Redux wire üzerinde de taşınsın)
        branchType: "lightingFixture"
      }
    };
    dispatch(addWire(wireAddPayload));

    // Three.js sahnesinde curved wire oluştur
    const wireGroup = new THREE.Group();

    // Vertex'leri Three.js Vector3 formatına dönüştür
    const vector3Vertices = vertices.map((v) => {
      return v.x !== undefined ? new THREE.Vector3(v.x, v.y, v.z || 0) : v;
    });

    // ✅ Düz mod (L tuşu): spline atlanır, vertex'ler doğrudan düz çizilir (WYSIWYG —
    // preview zaten düz olduğu için çizerken görünen hat aynen kalıcı olur).
    let geometry;
    if (lineModeRef.current) {
      geometry = new THREE.BufferGeometry().setFromPoints(vector3Vertices);
    } else {
      const curve = new THREE.CatmullRomCurve3(vector3Vertices);
      const divisions = Math.max(10, vertices.length * 10);
      const splinePoints = curve.getPoints(divisions);
      geometry = new THREE.BufferGeometry().setFromPoints(splinePoints);
    }
  const fixtureWireColorHex = settings?.fixture?.wireColor || '#0045ff';
  const fixtureWireColorNum = typeof fixtureWireColorHex === 'string' ? parseInt(fixtureWireColorHex.replace('#', ''), 16) : fixtureWireColorHex;
  const material = new THREE.LineBasicMaterial({ color: fixtureWireColorNum });
    const line = new THREE.Line(geometry, material);

    line.userData = {
      id: wireId,
      type: 'curvedLine',
      function: 'wireLightingFixture',
      isSelectable: true,
      connectedFrom: vector3Vertices[0],
      connectedTo: vector3Vertices[vector3Vertices.length - 1],
      parentLightingLoadId,
      lightingLineWireId,
      lineMode: lineModeRef.current, // ✅ Düz mod: reload'da spline atlanır (useWireRebuild)
      vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z || 0 })) // ✅ Kontrol noktaları (gizmo için)
    };

    wireGroup.userData = {
      wireID: wireId,
      parentLightingLoadId,
      lightingLineWireId,
      function: "simpleLightingFixture",
      power: totalPower,
      length: totalLength.toFixed(2),
      fixtureCount: fixtures.length
    };

    wireGroup.add(line);
    scene.add(wireGroup);

    // ✅ Başlangıç buatına fixture wire mapId'sini ekle
    const firstVertex = vertices[0];
    let startBuat = null;

    // Source'dan başlangıç buatını bul
    if (source && (source.type === "lightingBuat" || source.type === "buatCircle")) {
      startBuat = source;
    } else if (source && source.type === "lightSwitch") {
      // Anahtar ise, yakınındaki buatı bul
      sceneRef.current.traverse(obj => {
        if (obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') {
          const distance = obj.position.distanceTo(new THREE.Vector3(firstVertex.x, firstVertex.y, firstVertex.z || 0));
          if (distance < 5) {
            startBuat = obj;
          }
        }
      });
    }

    // Source yoksa, ilk vertex yakınındaki buatı bul
    if (!startBuat) {
      sceneRef.current.traverse(obj => {
        if (obj.userData?.type === 'lightingBuat' || obj.userData?.type === 'buatCircle') {
          const distance = obj.position.distanceTo(new THREE.Vector3(firstVertex.x, firstVertex.y, firstVertex.z || 0));
          if (distance < 5) {
            startBuat = obj;
          }
        }
      });
    }

    // Buata fixture wire mapId ekle
    if (startBuat) {
      // ✅ userData property read-only ise defineProperty ile zorla (frozen object hatası önleme)
      try {
        const newUserData = {
          ...startBuat.userData,
          fixtureWireMapIds: [
            ...(startBuat.userData.fixtureWireMapIds || []),
            wireId
          ]
        };

        // userData read-only olabilir, defineProperty ile override et
        Object.defineProperty(startBuat, 'userData', {
          value: newUserData,
          writable: true,
          configurable: true,
          enumerable: true
        });

       } catch (err) {
        console.warn('⚠️ Buat userData güncellenemedi:', err);
      }
    }

    // ✅ Fixture hattına wire marker'lar ekle (curve üzerinde)
    const rotateVector = (vector, angleRad) => {
      const cos = Math.cos(angleRad);
      const sin = Math.sin(angleRad);
      return new THREE.Vector3(
        vector.x * cos - vector.y * sin,
        vector.x * sin + vector.y * cos,
        0
      );
    };

    // ✅ wireMarker'lar — preview ile AYNI yerleşim (eğri üzerinde), markerGroup
    // (sürüklenebilir). FixtureCommand redo'da da AYNI fonksiyonu kullanır → tutarlı.
    const markerColorHex = settings?.fixture?.color || '#ffffff';
    const markerColorNum = typeof markerColorHex === 'string'
      ? parseInt(markerColorHex.replace('#', ''), 16) : markerColorHex;
    WireMarkerGenerator.createPermanentCurveMarkers(scene, vector3Vertices, wireId, markerColorNum, 2, lineModeRef.current);

    // Simple Supply'ye armatür feeder'ı ekle
    addLightingFixtureToSimpleSupply({
      parentLightingLoadId,
      fixtures,
      feederData: {
        id: generateUniqueId('lighting_fixture_feeder'),
        vertices,
        cable: { length: totalLength },
        wireId
      },
      totalPower
    });

    // ✅ floorArmatures güncelle: aktif katın armatür sayısını ve gücünü yaz
    if (parentLightingLoadId) {
      const currentFloorName = operationData?.continueFromShaft
        ? operationData?.currentFloorPlanName
        : activeFloorPlan?.name || null;
      if (currentFloorName) {
        // addLightingFixtureToSimpleSupply dispatch'i henüz reducer'a geçmemiş olabilir
        // bu yüzden mevcut load + yeni fixture bilgisini manuel hesapla
        const existingLoad = appStore.getState().simpleSupply.components.find(c => c.id === parentLightingLoadId);
        const existingFloorArm = existingLoad?.metadata?.floorArmatures || {};
        const prevFloor = existingFloorArm[currentFloorName] || { count: 0, power: 0 };
        dispatch(updateComponent({
          id: parentLightingLoadId,
          changes: {
            metadata: {
              floorArmatures: {
                ...existingFloorArm,
                [currentFloorName]: {
                  count: prevFloor.count + fixtures.length,
                  power: prevFloor.power + totalPower,
                }
              }
            }
          }
        }));
      }
    }

    // 🔵 Global Undo/Redo Transaction (useLighting pattern'i)
    // ✅ Runtime'da undoRedoManager'ı kontrol et (ref'ten veya window'dan)
    const undoRedoManager = undoRedoManagerRef.current || (typeof window !== 'undefined' ? window.undoRedoManager : null);

    if (undoRedoManager) {
      
      const tx = undoRedoManager.beginTransaction('Add Fixture Wire');

      // Restore callback: undo sonrası draw modunu yeniden aç (local undo — vertex'ler
      // tek tek geri alınabilsin diye). NOT: bu mekanizma KORUNMALI; kaldırılırsa undo
      // all-at-once olur. Polyline sorunu redraw preview'ın spline olmayışındandır.
      const restoreCallback = (restoreVertices, restoreSource, fixtureInfos) => {
        dispatch(setCommandType(COMMANDS.DRAW_FIXTURE));
        setTimeout(() => {
          dispatch(setOperationData({
            initialPoint: restoreVertices && restoreVertices[0] ? {
              x: restoreVertices[0].x,
              y: restoreVertices[0].y,
              z: restoreVertices[0].z || 0
            } : null,
            restoreVertices,
            fixtureInfos: fixtureInfos || [],
            source: restoreSource,
            _restoreId: Date.now()
          }));
        }, 16);
      };

      // Wire bilgisi
      const wireData = {
        id: wireId,
        sceneUUID: wireGroup.uuid,
        type: "curvedLine",
        vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
        layer: "lightingFixture",
        color: settings?.fixture?.wireColor || '#0045ff',
        parentLightingLoadId,
        lightingLineWireId
      };

      // Tüm fixture ID'lerini ve bilgilerini topla - scene'den bul
      const fixtureIds = [];
      const fixtureInfos = []; // ✅ Restore için fixture bilgileri

      fixtures.forEach(fixtureItem => {
        const compId = fixtureItem?.uuid || fixtureItem?.id || fixtureItem?.userData?.id || null;
        let foundObj = null;
        if (compId) {
          scene.traverse(obj => {
            if (!foundObj && obj.userData?.id === compId) foundObj = obj;
          });
        }

        const finalId = (foundObj && foundObj.userData?.id) || compId || fixtureItem?.uuid || fixtureItem?.id || generateUniqueId('fixture');
        fixtureIds.push(finalId);

        const pos = (foundObj && foundObj.position) || fixtureItem?.position || { x: 0, y: 0, z: 0 };
        const typ = fixtureItem?.type || foundObj?.userData?.fixtureType || 'normal';
        const pwr = fixtureItem?.power || foundObj?.userData?.power || 10;

        // ✅ Fixture restore bilgilerini kaydet
        fixtureInfos.push({
          id: compId,
          position: { x: pos.x, y: pos.y, z: pos.z || 0 },
          type: typ,
          power: pwr
        });
      });

      
      

      const sceneData = {
        wireId: wireId,
        wireUUID: wireGroup.uuid,
        fixtureIds: fixtureIds,
        fixtureInfos: fixtureInfos, // ✅ Fixture restore bilgileri
        vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z || 0 })),
        restoreVertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z || 0 })),
        restoreSource: source ? {
          type: source.type,
          parent: source.parent,
          userData: source.userData,
        } : null,
        restoreCommandType: COMMANDS.DRAW_FIXTURE,
        restoreCallback: restoreCallback, // ✅ local undo (vertex-by-vertex) — KORUNMALI
        parentLightingLoadId,
        lightingLineWireId
      };

      // Basit bir command oluştur - sadece sahne temizleme için
      const FixtureCommand = require('../../../modules/undoRedo').FixtureCommand;
      const cmd = new FixtureCommand('ADD', { id: generateUniqueId('fixture') }, wireData, sceneData);

      undoRedoManager.addCommand(cmd);

      undoRedoManager.commitTransaction(tx);
      

      // Ensure panel titles are refreshed for the parent lighting load
      try {
        if (parentLightingLoadId) {
          
          updatePanelTitles(scene, parentLightingLoadId);
        }
      } catch (err) {
        console.warn('⚠️ Fixture: updatePanelTitles failed', err);
      }
    } else {
      console.warn('⚠️ undoRedoManager is undefined - fixture eklendi ama undo-redo yok');
      console.warn('   undoRedoManagerRef.current:', undoRedoManagerRef.current);
      console.warn('   window.undoRedoManager:', typeof window !== 'undefined' ? window.undoRedoManager : 'window undefined');
    }

    // Temizlik
    dispatch(setCommandMessage("Aydınlatma armatürleri başarıyla eklendi"));
    dispatch(resetOperation());
    wiringActiveRef.current = false;

  };

  /**
   * 🎯 Ana effect - komut başlatma
   */
  useEffect(() => {
    if (commandType !== COMMANDS.DRAW_FIXTURE || !scene || !camera || !renderer) return;

    // simpleSupply'de lighting load varsa simple lighting fixture'i aktif et

    const hasLightingLoads = appStore.getState().simpleSupply.components.some(c => {

      return c.kind === COMPONENT_KINDS.LOAD &&
             (c.branchType === 'lighting' ||
              (c.name && c.name.includes('Aydınlatma')));
    });

    if (!hasLightingLoads) {
      return;
    }


    dispatch(setCommandMessage('🆕 SIMPLE LIGHTING FIXTURE: Aydınlatma buatlarına tıklayarak armatür hattı çizin, Space ile armatür ekleyin, Enter ile bitirin.'));
    sceneRef.current = scene;

    // Dynamic snap points oluştur (aydınlatma buatları, anahtarları ve vertex'ler)
    dynamicSnapPoints.current = [];
    scene.traverse(obj => {
      // Aydınlatma buatlarını snap point olarak ekle
      if (obj.userData?.type === 'buatCircle' || obj.userData?.type === 'lightingBuat') {
        dynamicSnapPoints.current.push({
          position: obj.position.clone(),
          type: 'lightingBuat',
          parent: obj.parent,
          no: obj.uuid,
          userData: obj.userData
        });
      }

      // Aydınlatma anahtarlarını snap point olarak ekle (son anahtar için önemli)
      if (obj.userData?.type === 'lightSwitch') {
        dynamicSnapPoints.current.push({
          position: obj.position.clone(),
          type: 'lightSwitch',
          parent: obj.parent,
          no: obj.uuid,
          userData: obj.userData
        });
      }

      // Aydınlatma hattı vertex'lerini de snap point olarak ekle
      if (obj.userData?.function === 'wireLighting' || obj.userData?.function === 'simpleLighting') {
        const wireVertices = obj.userData?.vertices || [];
        wireVertices.forEach((vertex, index) => {
          dynamicSnapPoints.current.push({
            position: new THREE.Vector3(vertex.x, vertex.y, vertex.z || 0),
            type: index === 0 ? 'start' : (index === wireVertices.length - 1 ? 'end' : 'vertex'),
            parent: obj,
            wireID: obj.userData?.wireID,
            lightingLoadId: obj.userData?.lightingLoadId
          });
        });
      }
    });

    wiringActiveRef.current = true;

    // Debug: Snap point tiplerini say
    const snapPointTypes = dynamicSnapPoints.current.reduce((acc, sp) => {
      acc[sp.type] = (acc[sp.type] || 0) + 1;
      return acc;
    }, {});
 

  }, [commandType, scene, camera, renderer, supplyComponentsLength]);

  /**
   * ⌨️ L tuşu — armatür çiziminde "düz mod" toggle. Sadece DRAW_FIXTURE aktifken.
   * useComponentWiring'e (mega-hook) dokunmadan ayrı, küçük bir listener.
   */
  useEffect(() => {
    if (commandType !== COMMANDS.DRAW_FIXTURE) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'l' && e.key !== 'L') return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      lineModeRef.current = !lineModeRef.current;
      dispatch(setCommandMessage(
        lineModeRef.current
          ? 'Armatür hattı: DÜZ mod açık (L ile eğriye dön)'
          : 'Armatür hattı: EĞRİ (spline) mod (L ile düze geç)'
      ));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [commandType, dispatch]);

  /**
   * 🎮 Wiring hook entegrasyonu - useComponentWiring kullanarak
   */
  useComponentWiring(
    scene,
    camera,
    renderer,
    [...snapPoints, ...dynamicSnapPoints.current],
    {
      ...fixtureConfig,
      // forward the global undo/redo manager so fixture can register restore callbacks
      undoRedoManagerRef,
      enabled: () => wiringActiveRef.current,
      onWireEnd: handleWireEnd,
      onItemAdd: (item) => {
       },
      onPreviewUpdate: () => {},
      onWireCancel: () => {
        wiringActiveRef.current = false;
        dispatch(resetOperation());
      }
    }
  );

  /**
   * 🎯 Fixture Gizmo hook entegrasyonu
   * Spline kontrol noktalarında gizmolar oluşturur
   */
  useFixtureGizmo(scene, camera, renderer?.domElement);
};

export default useFixture;